import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./CSS/HotelDetails.css";

const HOTEL_DETAIL_API = "https://api.hotel.namantechnolab.com/api/hotel/DetailSearch";

export default function HotelDetails() {
  const { tripId } = useParams();
  const [details, setDetails] = useState(null);
  const [hotelBasic, setHotelBasic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("Rooms");
  const [activeImage, setActiveImage] = useState(0);
  const [selectedRoomIdx, setSelectedRoomIdx] = useState(0);

  useEffect(() => {
    const storedHotel = sessionStorage.getItem("selectedHotel");
    if (storedHotel) {
      try {
        setHotelBasic(JSON.parse(storedHotel));
      } catch (err) {
        console.error("Could not parse selected hotel", err);
      }
    }

    if (tripId) {
      fetchHotelDetails(tripId);
    } else {
      setLoading(false);
      setError("No TripId found in URL.");
    }
  }, [tripId]);

  const fetchHotelDetails = async (id) => {
    setLoading(true);
    setError(null);

    const payload = { id: id };

    console.log("Hotel detail URL:", HOTEL_DETAIL_API);
    console.log("Hotel detail request payload:", payload);

    try {
      const res = await fetch(HOTEL_DETAIL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      console.log("Hotel detail response:", data);
      setDetails(data);

      try {
        sessionStorage.setItem("hotelDetailsResponse", JSON.stringify(data));
      } catch (err) {
        console.error("Could not persist hotel detail response", err);
      }
    } catch (err) {
      console.error("Hotel detail request failed:", err);
      setError(err.message || "Something went wrong while fetching hotel details.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Helpers mapped to actual API field names ----------

  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

  const hotelName = pick(hotelBasic?.HotelName, details?.hotelname, "Hotel Name");

  const starRating = Number(pick(hotelBasic?.StarRating, details?.HotelRating, 0));

  const address = pick(details?.HotelAddress, hotelBasic?.Address, "");

  const cityLine = [address, details?.CityName, details?.StateName, details?.CountryName]
    .filter(Boolean)
    .join(", ");

  // No explicit guest rating (out of 5) in API — derive a display rating from HotelRating (out of 5 stars)
  const rating = starRating || 5.0;

  const reviewCount = pick(hotelBasic?.ReviewCount, details?.ReviewCount, 1);

  const images = pick(details?.Images, hotelBasic?.Images, []) || [];

  const mainImage = images[activeImage] || null;

  const mapUrl =
    details?.Latitude && details?.Longititude
      ? `https://www.google.com/maps?q=${details.Latitude},${details.Longititude}`
      : null;

  const rooms = pick(details?.HotelRooms, []) || [];

  const selectedRoom = rooms[selectedRoomIdx] || rooms[0] || null;

  const getRoomName = (r) => pick(r?.RoomType, r?.RoomCategory, "Room");

  const getRoomOccupancy = (r) => pick(r?.Adults, 1);

  // API gives only one totalPrice (no separate MRP/strike price)
  const getOfferedPrice = (r) => Number(pick(r?.totalPrice, 0));

  const getMealBasis = (r) => pick(r?.Mealbasis, "Room Only");

  const getRoomImages = (r) => pick(r?.RommImages, []) || [];

  const policyNote = pick(
    details?.HotelPolicy?.length ? details.HotelPolicy.join(" ") : null,
    "Primary guest should be at least 18 years of age. Please carry a valid government-issued ID proof at check-in."
  );

  const facilities = pick(details?.HotelFacility, []) || [];

  const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  const tabs = ["Rooms", "Overview", "Amenities", "Location", "Booking Policy", "Guest Rating"];

  // ---------- Render states ----------

  if (loading) {
    return (
      <div className="centerScreen">
        <div className="spinner" />
        <p style={{ marginTop: 12, color: "#6b7280" }}>Loading hotel details…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="centerScreen">
        <p style={{ color: "#dc2626", fontWeight: 600 }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* ---------- Header card ---------- */}
      <div className="card headerCard">
        <div className="headerTop">
          <div>
            <div className="titleRow">
              <h1 className="hotelName">{hotelName}</h1>
              <span className="stars">
                {"★".repeat(Math.max(0, Math.min(5, starRating || 0)))}
              </span>
              <span className="badge">{pick(details?.PropertyType, "Hotel")}</span>
            </div>
            <div className="addressRow">
              <span>📍</span>
              <span>{cityLine || "Address not available"}</span>
              {mapUrl ? (
                <a href={mapUrl} target="_blank" rel="noreferrer" className="mapLink">
                  View on map
                </a>
              ) : (
                <span className="mapLink disabled">View on map</span>
              )}
            </div>
          </div>
          <div className="ratingBox">
            <div className="ratingLabel">
              {rating >= 4.5 ? "Excellent" : rating >= 3.5 ? "Very Good" : "Good"}
            </div>
            <div className="ratingReviews">{reviewCount} reviews</div>
            <div className="ratingPill">{rating.toFixed(1)}</div>
          </div>
        </div>

        {/* ---------- Gallery + booking card ---------- */}
        <div className="galleryRow">
          <div className="mainImageWrap">
            {mainImage ? (
              <img src={mainImage} alt={hotelName} className="mainImage" />
            ) : (
              <div className="imagePlaceholder">No image available</div>
            )}
            {images.length > 1 && (
              <>
                <button
                  className="navBtn navLeft"
                  onClick={() =>
                    setActiveImage((i) => (i - 1 + images.length) % images.length)
                  }
                >
                  ‹
                </button>
                <button
                  className="navBtn navRight"
                  onClick={() => setActiveImage((i) => (i + 1) % images.length)}
                >
                  ›
                </button>
              </>
            )}
          </div>

          <div className="thumbCol">
            {[1, 2, 3].map((offset) => {
              const idx = (activeImage + offset) % (images.length || 1);
              const src = images[idx];
              const isLast = offset === 3;
              return (
                <div key={offset} className="thumb" onClick={() => setActiveImage(idx)}>
                  {src ? <img src={src} alt="" /> : <div className="thumbPlaceholder" />}
                  {isLast && images.length > 4 && (
                    <div className="thumbOverlay">SEE ALL {images.length} PHOTOS</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bookingCard">
            {selectedRoom ? (
              <>
                <div className="roomName">{getRoomName(selectedRoom)}</div>
                <div className="roomMeta">
                  {getRoomOccupancy(selectedRoom)} x Guest | 1 x Room
                </div>
                <div className="freeCancel">✓ {getMealBasis(selectedRoom)}</div>

                <div className="priceBlock">
                  <div className="offerPrice">{formatINR(getOfferedPrice(selectedRoom))}</div>
                  <div className="perNight">Total price</div>
                </div>

                <div className="amenityTags">
                  {facilities.slice(0, 5).map((a, i) => (
                    <span className="amenityTag" key={i}>
                      ✓ {a}
                    </span>
                  ))}
                </div>

                <div className="ctaRow">
                  <button className="btnOutline">Select Rooms</button>
                  <button className="btnPrimary">Book Now</button>
                </div>
              </>
            ) : (
              <div className="noRooms">Room details not available.</div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Tabs ---------- */}
      <div className="card tabCard">
        {tabs.map((t) => (
          <button
            key={t}
            className={`tabBtn ${activeTab === t ? "active" : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ---------- Policy note ---------- */}
      <div className="card noticeCard">
        <span className="noticeIcon">ⓘ</span>
        <span>
          <strong>Please Note:</strong> {policyNote}
        </span>
      </div>

      {/* ---------- Overview tab ---------- */}
      {activeTab === "Overview" && details?.DetailDescription?.length > 0 && (
        <div className="card">
          {details.DetailDescription.map((d, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <h3 style={{ marginBottom: 6 }}>{d.Heading}</h3>
              {d.Details.map((p, j) => (
                <p key={j} style={{ whiteSpace: "pre-line", color: "#374151", fontSize: 14 }}>
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ---------- Amenities tab ---------- */}
      {activeTab === "Amenities" && (
        <div className="card">
          <div className="amenityTags" style={{ flexWrap: "wrap" }}>
            {facilities.map((f, i) => (
              <span className="amenityTag" key={i}>
                ✓ {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Room table ---------- */}
      {activeTab === "Rooms" && (
        <div className="card tableCard">
          <div className="tableHeader">
            <div>Room Type</div>
            <div>Benefits</div>
            <div>Per Night Price</div>
            <div></div>
          </div>

          {rooms.length ? (
            rooms.map((r, idx) => (
              <div className="tableRow" key={r.optionsId || idx}>
                <div className="roomCol">
                  {getRoomImages(r)[0] && (
                    <img src={getRoomImages(r)[0]} alt={getRoomName(r)} />
                  )}
                  <div className="roomColName">{getRoomName(r)}</div>
                </div>

                <div className="benefitsCol">
                  <div>✓ {getMealBasis(r)}</div>
                  <div>✓ {r.Adults} Adult{Number(r.Adults) > 1 ? "s" : ""}</div>
                  {r.SmokingAllowed === false && <div>✓ Non-Smoking</div>}
                </div>

                <div className="priceCol">
                  <div className="offerPrice small">{formatINR(getOfferedPrice(r))}</div>
                  <div className="perNight small">Total price</div>
                </div>

                <div className="actionCol">
                  <button className="btnPrimary small" onClick={() => setSelectedRoomIdx(idx)}>
                    Book Now
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="noRoomsRow">No room options available for this hotel.</div>
          )}
        </div>
      )}
    </div>
  );
}